import type { CornerRadius } from "../configs/types";

/** Per-corner radii fully resolved to numbers (no optional fields). */
export type ResolvedCorners = {
  tl: number;
  tr: number;
  br: number;
  bl: number;
};

/**
 * Normalizes the flexible `targetRadius` prop into explicit per-corner values.
 * `undefined` falls back to a uniform `fallback`; a number rounds all corners
 * equally; an object rounds them individually with omitted corners at 0.
 */
export function resolveCorners(
  radius: number | CornerRadius | undefined,
  fallback: number
): ResolvedCorners {
  if (radius === undefined) {
    return { tl: fallback, tr: fallback, br: fallback, bl: fallback };
  }
  if (typeof radius === "number") {
    return { tl: radius, tr: radius, br: radius, bl: radius };
  }
  return {
    tl: radius.topLeft ?? 0,
    tr: radius.topRight ?? 0,
    br: radius.bottomRight ?? 0,
    bl: radius.bottomLeft ?? 0
  };
}

/** True when every corner shares the same radius (an SVG `<rect rx>` suffices). */
export function isUniform(corners: ResolvedCorners): boolean {
  return (
    corners.tl === corners.tr &&
    corners.tr === corners.br &&
    corners.br === corners.bl
  );
}

const clamp = (value: number, max: number): number =>
  Math.max(0, Math.min(value, max));

/**
 * Builds an SVG path `d` string for a rectangle with independent corner radii.
 * Each radius is clamped to half the shorter side so corners never overlap.
 * A zero-radius corner draws a sharp line instead of an arc.
 */
export function buildRoundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  corners: ResolvedCorners
): string {
  const max = Math.min(width, height) / 2;
  const tl = clamp(corners.tl, max);
  const tr = clamp(corners.tr, max);
  const br = clamp(corners.br, max);
  const bl = clamp(corners.bl, max);
  const right = x + width;
  const bottom = y + height;

  return [
    `M ${x + tl} ${y}`,
    `H ${right - tr}`,
    tr ? `A ${tr} ${tr} 0 0 1 ${right} ${y + tr}` : `L ${right} ${y}`,
    `V ${bottom - br}`,
    br ? `A ${br} ${br} 0 0 1 ${right - br} ${bottom}` : `L ${right} ${bottom}`,
    `H ${x + bl}`,
    bl ? `A ${bl} ${bl} 0 0 1 ${x} ${bottom - bl}` : `L ${x} ${bottom}`,
    `V ${y + tl}`,
    tl ? `A ${tl} ${tl} 0 0 1 ${x + tl} ${y}` : `L ${x} ${y}`,
    "Z"
  ].join(" ");
}
